import { useNavigate, useParams, generatePath } from 'react-router'
import { Alert, Box, CircularProgress, Container, Tab, Tabs, Typography } from '@mui/material'
import { TabDisplay } from 'Components/TabDisplay'
import { BackArrow } from 'Assets/SVGs/back-arrow'
import { CAREGIVER_PORTAL_MEMBERS_CHILD_DETAILS } from 'Pages/Routes'
import { EnrollmentDetails } from 'Components/Enrollment/EnrollmentDetails'
import { ProviderDetails } from 'Components/Enrollment/ProviderDetails'
import { useEffect, useState } from 'react'
import { sendGet } from 'hooks/use-fetch'
import { CircleCheck } from 'Assets/SVGs/CircleCheck'
import { DocumentRequirementList, isSatisfied } from 'Components/Documents'
import { useServiceDocuments } from './useServiceDocuments'

interface EnrollmentInfo {
  childID: number
  serviceProviderID: number
  facilityName: string | null
}

const MISSING_MESSAGE = 'You are currently missing documents that are required for this service. Please resolve this issue to ensure timely enrollments.'

export function ServiceDetails (): JSX.Element {
  const nav = useNavigate()
  const { enrollmentId } = useParams()
  const [isLoading, setIsLoading] = useState(true)
  const [tab, setTab] = useState(0)
  const [enrollment, setEnrollment] = useState<EnrollmentInfo | null>(null)
  const documents = useServiceDocuments(Number(enrollmentId))
  const missingDocuments = documents.data?.requirements.some(r => !isSatisfied(r)) ?? false

  const warningNotificationStyle = {
    backgroundColor: '#bc3656',
    color: 'white',
    padding: '1px 2px 1px 2px',
    borderRadius: '14px',
    minWidth: '16px'
  }

  useEffect(() => {
    const getData = async (): Promise<void> => {
      const { response, success } = await sendGet(`/CaregiverPortal/GetEnrollmentInfo/${enrollmentId ?? 0}`, {})
      if (success) {
        setEnrollment(response)
      }
      setIsLoading(false)
    }

    void getData()
  }, [enrollmentId])

  // back to wherever the service was opened from (the dashboard or a child's details); a page opened
  // directly has no in-app history (react-router's history idx is 0), so fall back to the child
  const goBack = (childID: number): void => {
    if ((window.history.state?.idx ?? 0) > 0) {
      nav(-1)
    } else {
      nav(generatePath(CAREGIVER_PORTAL_MEMBERS_CHILD_DETAILS, { childId: childID.toString() }))
    }
  }

  if (isLoading) {
    return <CircularProgress />
  } else if (enrollment == null) {
    return <Box>There was an error loading the service details.</Box>
  } else {
    return <Container>
      <Box sx={{ mt: '24px', mb: '24px', display: 'flex' }}>
        <Box onClick={() => { goBack(enrollment.childID) }} sx={{ cursor: 'pointer' }} data-testid='backButton'>
          <BackArrow />
        </Box>
        <Typography variant='h5' sx={{ fontWeight: 600, ml: '5px' }} component='div'>
          Service Details
        </Typography>
      </Box>
      {missingDocuments && tab !== 1 && <Alert severity='error' sx={{ mb: '16px' }} data-testid='documentsMissingBanner'>{MISSING_MESSAGE}</Alert>}

      <Tabs value={tab}
        data-testid='tabs'
        onChange={(e, val) => { setTab(val) }}
        aria-label="Care Network Detail Tabs"
        textColor="secondary"
        indicatorColor="secondary"
      >
        <Tab label="Details" value={0} data-testid="details" />
        <Tab label="Documents" value={1} data-testid="documents" icon={(missingDocuments ? <Box sx={warningNotificationStyle} >!</Box> : <Box sx={{ mt: '4px' }} > <CircleCheck fill="#0e8476" size={18} /></Box>)} iconPosition="end" />
      </Tabs>

      <TabDisplay index={0} value={tab}>
        <Box sx={{ mt: 1 }}>
          <EnrollmentDetails enrollmentID={Number(enrollmentId)} canReview={false} readOnly={true} />
        </Box>
        <Box sx={{ mt: 1 }}>
          <ProviderDetails enrollmentID={Number(enrollmentId)} buildingName={enrollment.facilityName} />
        </Box>
      </TabDisplay>

      <TabDisplay index={1} value={tab}>
        <Box sx={{ mt: 1 }}>
          {documents.isLoading && <CircularProgress />}
          {!documents.isLoading && documents.data === null && <Box>There was an error loading this service's documents.</Box>}
          {documents.data !== null && <DocumentRequirementList
            data={documents.data}
            onAdd={documents.addDocument}
            missingMessage={MISSING_MESSAGE}
            satisfiedMessage='All required documents have been uploaded successfully.'
            emptyMessage='This service does not require any documents.'
          />}
        </Box>
      </TabDisplay>
    </Container>
  }
}
